import React, { useState, useEffect } from 'react'
import MuiDialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import Select from '@material-ui/core/Select'
import MuiMenuItem from '@material-ui/core/MenuItem'
import Checkbox from '@material-ui/core/Checkbox'
import FormGroup from '@material-ui/core/FormGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import Spinner from '../components/spinner'
import { useQuery, useMutation, useApolloClient } from '@apollo/client'
import {
    ProductsDocument,
    ProductsQuery,
    DeleteMenuItemDocument,
    DeleteMenuItemMutation,
    MenuItemsByMachineIdDocument,
} from '../generated'

type Machine = {
    id: string
    location: string
    name: string
}

type MenuItemDeleteFromMachinesProps = {
    open: boolean
    onClose: () => void
    machines: Machine[]
}

const MenuItemDeleteFromMachines = ({
    open,
    onClose,
    machines,
}: MenuItemDeleteFromMachinesProps) => {
    const { data: productsData, loading: productsLoading, error: productsError } = useQuery<ProductsQuery>(ProductsDocument, { variables: { activeOnly: true } })

    const [perMachineMenuItems, setPerMachineMenuItems] = useState<Record<string, { id: string, productId: string }[]>>({})
    const [menuItemsLoading, setMenuItemsLoading] = useState(false)
    const [menuItemsError, setMenuItemsError] = useState<any>(null)

    const [selectedProductId, setSelectedProductId] = useState<string>('')
    const [selectedMachineIds, setSelectedMachineIds] = useState<string[]>([])
    const [deleting, setDeleting] = useState(false)
    const [progress, setProgress] = useState<number>(0)
    const [deleteMenuItem] = useMutation<DeleteMenuItemMutation>(DeleteMenuItemDocument)
    const client = useApolloClient()

    let allMachineIds: string[] = []
    let sortedMachines: Machine[] = []
    if (Array.isArray(machines)) {
        allMachineIds = machines.map((m) => m.id)
        sortedMachines = machines.slice().sort((a, b) => (a.location || '').localeCompare(b.location || ''))
    }

    let sortedProducts: ProductsQuery['products'] = []
    if (productsData && Array.isArray(productsData.products)) {
        sortedProducts = productsData.products.slice().sort((a, b) => {
            const getEnName = (p: ProductsQuery['products'][0]) =>
                (p.texts ?? []).find((t) => String(t.locale).toLowerCase() === 'en')?.name ?? '(no English name)'
            return getEnName(a).localeCompare(getEnName(b))
        })
    }

    // Fetch menu items for all machines when machines change
    useEffect(() => {
        let cancelled = false
        async function fetchAll() {
            if (!Array.isArray(machines) || machines.length === 0) {
                setPerMachineMenuItems({})
                return
            }
            setMenuItemsLoading(true)
            setMenuItemsError(null)
            try {
                const results = []
                for (const machine of machines) {
                    const res = await client.query({
                        query: MenuItemsByMachineIdDocument,
                        variables: { input: { machineId: machine.id, includeUnavailable: true } },
                        fetchPolicy: 'network-only',
                    })
                    results.push([machine.id, res.data.menuItemsByMachineId.map((mi: any) => ({ id: mi.id, productId: mi.product.id }))])
                }
                if (!cancelled) {
                    setPerMachineMenuItems(Object.fromEntries(results))
                }
            } catch (err) {
                if (!cancelled) setMenuItemsError(err)
            } finally {
                if (!cancelled) setMenuItemsLoading(false)
            }
        }
        fetchAll()
        return () => { cancelled = true }
    }, [machines, client])

    // Build a map: machineId -> Set of productIds already present
    const machineProductMap: Record<string, Set<string>> = {}
    for (const mid in perMachineMenuItems) {
        machineProductMap[mid] = new Set(perMachineMenuItems[mid].map((mi) => mi.productId))
    }

    // Checkbox logic
    const isMachineDisabled = (mid: string) => {
        if (!selectedProductId) return true
        return !machineProductMap[mid]?.has(selectedProductId)
    }
    const enabledMachineIds = allMachineIds.filter((mid) => !isMachineDisabled(mid))
    const allChecked = enabledMachineIds.length > 0 && enabledMachineIds.every((mid) => selectedMachineIds.includes(mid))
    const someChecked = enabledMachineIds.some((mid) => selectedMachineIds.includes(mid)) && !allChecked

    const handleAllMachinesCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedMachineIds((prev) => [
                ...prev.filter((mid) => isMachineDisabled(mid)),
                ...enabledMachineIds,
            ])
        } else {
            setSelectedMachineIds((prev) => prev.filter((mid) => isMachineDisabled(mid)))
        }
    }

    const handleMachineCheckboxChange = (id: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedMachineIds((prev) => prev.includes(id) ? prev : [...prev, id])
        } else {
            setSelectedMachineIds((prev) => prev.filter((mid) => mid !== id))
        }
    }

    // Delete logic
    const handleDelete = async () => {
        setDeleting(true)
        setProgress(0)
        try {
            for (let i = 0; i < selectedMachineIds.length; i++) {
                const machineId = selectedMachineIds[i]
                const menuItem = perMachineMenuItems[machineId]?.find((mi) => mi.productId === selectedProductId)
                if (menuItem) {
                    await deleteMenuItem({
                        variables: { id: menuItem.id },
                        refetchQueries: [
                            {
                                query: MenuItemsByMachineIdDocument,
                                variables: { input: { machineId, includeUnavailable: true } },
                            },
                        ],
                    })
                }
                setProgress(i + 1)
            }
            setSelectedProductId('')
            setSelectedMachineIds([])
            setProgress(0)
            onClose()
        } finally {
            setDeleting(false)
        }
    }

    return (
        <MuiDialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Delete menu items from machines</DialogTitle>
            <DialogContent dividers>
                {productsLoading && <Spinner />}
                {productsError && (
                    <div>Error loading products: {JSON.stringify(productsError, null, 2)}</div>
                )}
                {!productsLoading && !productsError && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                        <FormControl style={{ minWidth: 300 }}>
                            <InputLabel id="product-select-label">Product</InputLabel>
                            <Select
                                labelId="product-select-label"
                                value={selectedProductId}
                                onChange={(e) => setSelectedProductId(e.target.value as string)}
                            >
                                {sortedProducts.map((p) => (
                                    <MuiMenuItem key={p.id} value={p.id}>
                                        {(p.texts ?? []).find((t) => String(t.locale).toLowerCase() === 'en')?.name ?? '(no English name)'}
                                    </MuiMenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                )}

                {/* Machines list */}
                <div style={{ marginTop: 16 }}>
                    {menuItemsLoading ? <Spinner /> : null}
                    {menuItemsError && (
                        <div>
                            Error loading menu items: {JSON.stringify(menuItemsError, null, 2)}
                        </div>
                    )}
                    {!menuItemsLoading && !menuItemsError && (
                        <>
                            <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
                                Select machines:
                            </Typography>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={allChecked}
                                            indeterminate={someChecked}
                                            onChange={handleAllMachinesCheckboxChange}
                                            color="primary"
                                            disabled={enabledMachineIds.length === 0}
                                        />
                                    }
                                    label="All"
                                />
                                <Divider style={{ margin: '8px 0' }} />
                                {sortedMachines.map((machine) => (
                                    <FormControlLabel
                                        key={machine.id}
                                        control={
                                            <Checkbox
                                                checked={selectedMachineIds.includes(machine.id)}
                                                onChange={handleMachineCheckboxChange(machine.id)}
                                                value={machine.id}
                                                color="primary"
                                                disabled={isMachineDisabled(machine.id)}
                                            />
                                        }
                                        label={
                                            <span>
                                                <span style={{ fontWeight: 500 }}>
                                                    {machine.location}
                                                </span>
                                                {machine.name ? ` – ${machine.name}` : ''}
                                                {isMachineDisabled(machine.id) && (
                                                    <span style={{ color: '#c00', marginLeft: 8 }}>
                                                        (no such product)
                                                    </span>
                                                )}
                                            </span>
                                        }
                                    />
                                ))}
                            </FormGroup>
                        </>
                    )}
                    {deleting && (
                        <div style={{ marginTop: 16 }}>
                            <Typography variant="body2" color="textSecondary">
                                Deleting {progress} of {selectedMachineIds.length}...
                            </Typography>
                            <Spinner />
                        </div>
                    )}
                </div>
            </DialogContent>
            <DialogActions style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button
                    onClick={() => {
                        onClose()
                        setSelectedProductId('')
                        setSelectedMachineIds([])
                        setProgress(0)
                    }}
                    disabled={deleting}
                >
                    Cancel
                </Button>
                <Button
                    color="secondary"
                    variant="contained"
                    onClick={handleDelete}
                    disabled={
                        deleting ||
                        !selectedProductId ||
                        selectedMachineIds.length === 0
                    }
                >
                    {deleting ? 'Deleting…' : 'Delete'}
                </Button>
            </DialogActions>
        </MuiDialog>
    )
}

export default MenuItemDeleteFromMachines